import React, { useState } from 'react';
import { Button, Card, Table, Upload, message } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  GET_PARTNER_PENALTY_UPLOAD_TASKS,
  GET_PARTNER_PENALTY_RESULT_DOWNLOAD_URL,
  DOWNLOAD_PARTNER_PENALTY_UPLOAD_TEMPLATE,
} from './graphql/queries';
import { SUBMIT_PARTNER_PENALTY_UPLOAD } from './graphql/mutations';
import type {
  PartnerPenaltyUploadTaskType,
  GetPartnerPenaltyUploadTasksResponse,
  GetPartnerPenaltyResultDownloadUrlResponse,
} from './graphql/types';

const TEMPLATE_NAME = 'partner_penalty_template.xlsx';
const MAX_FILE_MB = 50;

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') { reject(new Error('Failed to read file')); return; }
      const comma = dataUrl.indexOf(',');
      if (comma === -1) { reject(new Error('Failed to read file')); return; }
      resolve(dataUrl.slice(comma + 1));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });


const PenaltyGenerationPage: React.FC = () => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);

  const { data: tasksData, loading: tasksLoading, refetch: refetchTasks } =
    useQuery<GetPartnerPenaltyUploadTasksResponse>(GET_PARTNER_PENALTY_UPLOAD_TASKS, {
      fetchPolicy: 'cache-and-network',
    });

  const [submitUpload] = useMutation(SUBMIT_PARTNER_PENALTY_UPLOAD);

  const [fetchTemplate] = useLazyQuery<{ downloadPartnerPenaltyUploadTemplate: string }>(
    DOWNLOAD_PARTNER_PENALTY_UPLOAD_TEMPLATE,
    { fetchPolicy: 'network-only' },
  );

  const [fetchDownloadUrl] = useLazyQuery<GetPartnerPenaltyResultDownloadUrlResponse>(
    GET_PARTNER_PENALTY_RESULT_DOWNLOAD_URL,
    { fetchPolicy: 'network-only' },
  );

  const tasks = tasksData?.getPartnerPenaltyUploadTasks ?? [];

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      const res = await fetchTemplate();
      const base64 = res.data?.downloadPartnerPenaltyUploadTemplate;
      if (!base64) { message.warning(t('partnerControl.penaltyGeneration.downloadUnavailable')); return; }
      const bytes = atob(base64);
      const buffer = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = TEMPLATE_NAME;
      a.style.visibility = 'hidden';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch {
      message.error(t('partnerControl.penaltyGeneration.downloadUnavailable'));
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleUpload = async (file: File) => {
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    if (!isExcel) {
      message.error(t('partnerControl.penaltyGeneration.onlyExcel'));
      return false;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      message.error(t('partnerControl.penaltyGeneration.fileTooLarge', { mb: MAX_FILE_MB }));
      return false;
    }

    setUploading(true);
    message.loading({ content: t('partnerControl.penaltyGeneration.uploading'), key: 'pp-upload', duration: 0 });

    try {
      const fileBase64 = await readFileAsBase64(file);
      const result = await submitUpload({ variables: { fileBase64, fileName: file.name } });
      message.destroy('pp-upload');
      const taskId = result.data?.submitPartnerPenaltyUpload;
      message.success(t('partnerControl.penaltyGeneration.uploadSuccess', { taskId }));
      refetchTasks();
    } catch (err: unknown) {
      message.destroy('pp-upload');
      message.error(t('partnerControl.penaltyGeneration.uploadFailed', {
        error: (err as Error)?.message ?? 'Unknown error',
      }));
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleDownloadResult = async (record: PartnerPenaltyUploadTaskType) => {
    const res = await fetchDownloadUrl({ variables: { taskId: Number(record.id) } });
    const url = res.data?.getPartnerPenaltyResultDownloadUrl;
    if (!url) { message.warning(t('partnerControl.penaltyGeneration.downloadUnavailable')); return; }
    const a = document.createElement('a');
    a.href = url;
    a.download = record.resultFileName ?? 'result.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const getStatusText = (record: PartnerPenaltyUploadTaskType): { text: string; color: string } => {
    switch (record.status) {
      case 'UPLOADED': return { text: t('partnerControl.penaltyGeneration.status.uploaded'), color: '' };
      case 'PARSING': return { text: t('partnerControl.penaltyGeneration.status.parsing'), color: '' };
      case 'COMPLETED': return { text: t('partnerControl.penaltyGeneration.status.completed'), color: 'text-green-600' };
      case 'FAILED': return {
        text: record.failureReason
          ? t(record.failureReason, { defaultValue: record.failureReason })
          : t('partnerControl.penaltyGeneration.status.failed'),
        color: 'text-red-500',
      };
      default: return { text: record.status, color: '' };
    }
  };

  const columns: ColumnsType<PartnerPenaltyUploadTaskType> = [
    {
      title: t('partnerControl.penaltyGeneration.column.fileName'),
      dataIndex: 'sourceFileName',
      key: 'sourceFileName',
      width: 220,
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: t('partnerControl.penaltyGeneration.column.sourceType'),
      key: 'penaltyTypeLabels',
      width: 300,
      render: (_, r) =>
        r.penaltyTypeLabels.length > 0 ? r.penaltyTypeLabels.join(' / ') : '—',
    },
    {
      title: t('partnerControl.penaltyGeneration.column.submitter'),
      dataIndex: 'submitterName',
      key: 'submitterName',
      width: 120,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: t('partnerControl.penaltyGeneration.column.importTime'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: t('partnerControl.penaltyGeneration.column.successFail'),
      key: 'counts',
      width: 120,
      render: (_, r) => (
        <span>
          <span className="text-green-600">{r.successCount}</span>
          {' / '}
          <span className={r.failCount > 0 ? 'text-red-500' : ''}>{r.failCount}</span>
        </span>
      ),
    },
    {
      title: t('partnerControl.penaltyGeneration.column.status'),
      key: 'status',
      width: 160,
      render: (_, r) => {
        const { text, color } = getStatusText(r);
        return <span className={color}>{text}</span>;
      },
    },
    {
      title: t('partnerControl.penaltyGeneration.column.action'),
      key: 'action',
      width: 140,
      render: (_, r) => {
        if (r.failCount > 0 && (r.status === 'COMPLETED' || r.status === 'FAILED')) {
          return (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadResult(r)}
            >
              {t('partnerControl.penaltyGeneration.downloadResult')}
            </Button>
          );
        }
        return <span className="text-gray-400">—</span>;
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="text-2xl font-bold">{t('partnerControl.penaltyGeneration.title')}</div>
        <div className="text-gray-500 mt-1">{t('partnerControl.penaltyGeneration.subtitle')}</div>
      </div>

      <Card
        title={t('partnerControl.penaltyGeneration.uploadCard')}
        extra={
          <div className="flex gap-2">
            <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate} loading={templateLoading}>
              {t('partnerControl.penaltyGeneration.downloadTemplate')}
            </Button>
            {/* todo gf 手动新增功能待实现，暂时隐藏 */}
            {/* <Button disabled>{t('partnerControl.penaltyGeneration.manualAdd')}</Button> */}
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              beforeUpload={(file) => { handleUpload(file); return false; }}
              disabled={uploading}
            >
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={uploading}
              >
                {t('partnerControl.penaltyGeneration.batchImport')}
              </Button>
            </Upload>
          </div>
        }
      >
        <Upload.Dragger
          accept=".xlsx,.xls"
          showUploadList={false}
          beforeUpload={(file) => { handleUpload(file); return false; }}
          disabled={uploading}
          className="mb-0"
        >
          <p className="ant-upload-drag-icon"><UploadOutlined /></p>
          <p className="ant-upload-text">{t('partnerControl.penaltyGeneration.uploadHint')}</p>
          <p className="ant-upload-hint">{t('partnerControl.penaltyGeneration.uploadHintSub')}</p>
        </Upload.Dragger>
      </Card>

      <Card
        title={t('partnerControl.penaltyGeneration.recordsCard')}
        extra={
          <Button onClick={() => refetchTasks()} loading={tasksLoading}>
            {t('partnerControl.penaltyGeneration.refresh')}
          </Button>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={tasks}
          loading={tasksLoading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: t('partnerControl.penaltyGeneration.noRecords') }}
        />
      </Card>
    </div>
  );
};

export default PenaltyGenerationPage;
